import {
	isCollectionInSnipeRollout,
	protocolFromInscription,
	snipeProtectionRolloutCollections,
	type SecureAssetProtocol,
	type SecurePurchaseCapabilities
} from './secure-purchase'

/**
 * Snipe protection is mandatory for assets it can cover.
 *
 * PRs #595 and #597 caught `secure_listing_unavailable` (and the OKX key-path
 * signing errors) after `build-bulk` had already succeeded, then posted a
 * standard escrow for the same items. The authorize/build retries (0.8s + 2s,
 * plus the 1.5s build retry) put that escrow 6–15s after the successful
 * protected build. Supported assets never take that path.
 */
export const SNIPE_TEMPORARILY_UNAVAILABLE =
	'Snipe protection is temporarily unavailable, please try again'

export const NO_SNIPE_PROTECTION_NOTE = 'No snipe protection for this asset type'

/** Passthrough v4 cannot move an output below this postage. */
export const MIN_PROTECTED_POSTAGE_SATS = 330

export type ProtectionAsset = {
	protocol?: SecureAssetProtocol
	slug?: string | null
	rune?: boolean
	alkane?: boolean
	brc20?: boolean
	sharedUtxoCount?: number
	postageSats?: number | null
}

export type ProtectionSupport =
	| { supported: true }
	| { supported: false; reason: string; shape: boolean; code?: string }

export type SnipeAvailability = 'loading' | 'offered' | 'disabled' | 'probe_failed'

export type ListingPlan =
	| { action: 'protect' }
	| { action: 'block'; message: string }
	| { action: 'confirm'; message: string }
	| { action: 'standard'; note: string; shape: boolean }

const SHAPE_CODES = new Set(['ambiguous_inscription_assets', 'postage_too_small'])

/**
 * Codes that mean the protection service itself failed. They used to be
 * `SNIPE_UNAVAILABLE_CODES` and downgraded to a standard listing. They block.
 */
const PROTECTION_DOWN_CODES = new Set([
	'secure_listing_unavailable',
	'build3_unavailable',
	'capabilities_fetch_failed',
	'capabilities_error',
	'missing_secure_purchase',
	'invalid_capabilities',
	'cosigner_unavailable',
	'signer_quorum_unavailable',
	'invalid_rollout_configuration',
	'unsupported_wallet',
	'unsupported_protocol',
	'protocol_extraction_unverified'
])

export function protocolProtectionEnabled(
	protocol: SecureAssetProtocol,
	caps?: Pick<SecurePurchaseCapabilities, 'protocols' | 'protocol_status'> | null
): boolean {
	if (!caps) return protocol === 'ordinal'
	const status = caps.protocol_status?.[protocol]
	if (status === 'enabled') return true
	// A recorded status other than enabled (disabled_by_rollout,
	// protocol_extraction_unverified, …) wins over the protocols list.
	if (typeof status === 'string') return false
	if (Array.isArray(caps.protocols) && caps.protocols.length > 0) {
		return caps.protocols.includes(protocol)
	}
	return protocol === 'ordinal'
}

export function assetProtocol(asset: ProtectionAsset): SecureAssetProtocol {
	if (asset.protocol) return asset.protocol
	return protocolFromInscription({
		brc20: asset.brc20 ? { ticker: 'x' } : undefined,
		collection: asset.slug ? { slug: asset.slug } : undefined,
		rune: asset.rune,
		alkane: asset.alkane
	})
}

function shapeReason(code: string): string {
	if (code === 'postage_too_small') {
		return 'No snipe protection for this output: postage is too low.'
	}
	return 'No snipe protection for this output: it shares a UTXO with other inscriptions.'
}

/**
 * Whether protection can move this asset. Ordinals are supported. BRC-20,
 * TAP (including DMT-NAT), runes and alkanes are unsupported while
 * `protocol_status` / `protocols` only enable `ordinal`. Shared UTXOs and
 * low postage are unsupported shape, not an asset-type exemption.
 */
export function protectionSupport(
	asset: ProtectionAsset,
	caps?: Pick<SecurePurchaseCapabilities, 'protocols' | 'protocol_status'> | null
): ProtectionSupport {
	if ((asset.sharedUtxoCount ?? 0) > 1) {
		return {
			supported: false,
			shape: true,
			code: 'ambiguous_inscription_assets',
			reason: shapeReason('ambiguous_inscription_assets')
		}
	}
	if (asset.postageSats != null && asset.postageSats < MIN_PROTECTED_POSTAGE_SATS) {
		return {
			supported: false,
			shape: true,
			code: 'postage_too_small',
			reason: shapeReason('postage_too_small')
		}
	}
	if (!protocolProtectionEnabled(assetProtocol(asset), caps)) {
		return { supported: false, shape: false, reason: NO_SNIPE_PROTECTION_NOTE }
	}
	return { supported: true }
}

export function postageSatsOf(row: unknown): number | null {
	if (!row || typeof row !== 'object') return null
	const record = row as {
		postageSats?: unknown
		postage?: unknown
		outpoint?: { sats?: unknown } | string
	}
	const nested =
		record.outpoint && typeof record.outpoint === 'object' ? record.outpoint.sats : undefined
	for (const value of [record.postageSats, record.postage, nested]) {
		const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN
		if (Number.isFinite(n) && n >= 0) return n
	}
	return null
}

export function protectionAssetFromRow(
	row: any,
	slug?: string | null,
	extra?: { rune?: boolean; alkane?: boolean }
): ProtectionAsset {
	const collectionSlug =
		slug || row?.collection?.slug || row?.collection_slug || null
	const shared = Number(row?.shared_utxo_count)
	return {
		protocol: protocolFromInscription({
			brc20: row?.brc20,
			collection: collectionSlug ? { slug: collectionSlug } : row?.collection,
			rune: Boolean(extra?.rune || row?.rune),
			alkane: Boolean(extra?.alkane || row?.alkane)
		}),
		slug: collectionSlug,
		rune: Boolean(extra?.rune || row?.rune),
		alkane: Boolean(extra?.alkane || row?.alkane),
		brc20: Boolean(row?.brc20?.ticker),
		sharedUtxoCount: Number.isFinite(shared) ? shared : undefined,
		postageSats: postageSatsOf(row)
	}
}

/**
 * Collection rollout is config. It must not be confused with a failed probe:
 * a probe failure stays `probe_failed` even when the slug would be outside
 * the dial.
 */
export function availabilityForCollection(
	status: SnipeAvailability,
	slug: string | null | undefined,
	rollout = snipeProtectionRolloutCollections()
): SnipeAvailability {
	if (status !== 'offered') return status
	return isCollectionInSnipeRollout(slug, rollout) ? 'offered' : 'disabled'
}

export function resolveListingPlan(input: {
	asset: ProtectionAsset
	availability: SnipeAvailability
	caps?: Pick<SecurePurchaseCapabilities, 'protocols' | 'protocol_status'> | null
	shapeConfirmed?: boolean
}): ListingPlan {
	const support = protectionSupport(input.asset, input.caps)
	const protocol = assetProtocol(input.asset)
	// A successful probe that reports ordinal as anything other than enabled
	// must not downgrade. Ordinals are protected, or blocked.
	if (protocol === 'ordinal' && input.caps && !protocolProtectionEnabled('ordinal', input.caps)) {
		return { action: 'block', message: SNIPE_TEMPORARILY_UNAVAILABLE }
	}
	// Asset types protection does not cover list standard even when the
	// capabilities probe has not answered. With no caps, only ordinal is
	// treated as protected, including when a shape flag is also set.
	if (!protocolProtectionEnabled(protocol, input.caps)) {
		return { action: 'standard', note: NO_SNIPE_PROTECTION_NOTE, shape: false }
	}
	if (input.availability === 'probe_failed' || input.availability === 'loading') {
		return { action: 'block', message: SNIPE_TEMPORARILY_UNAVAILABLE }
	}
	if (!support.supported && support.shape) {
		if (!input.shapeConfirmed) return { action: 'confirm', message: support.reason }
		return { action: 'standard', note: support.reason, shape: true }
	}
	if (!support.supported) {
		return { action: 'standard', note: support.reason, shape: false }
	}
	if (input.availability !== 'offered') {
		return { action: 'block', message: SNIPE_TEMPORARILY_UNAVAILABLE }
	}
	return { action: 'protect' }
}

/**
 * What to do after a protected build/sign/authorize already ran. A failure
 * here must not create a standard listing for a supported asset.
 * `secure_listing_unavailable` blocks even when the seller has confirmed a
 * shape exception.
 */
export function planForProtectedFailure(
	code: string,
	opts: {
		shapeConfirmed?: boolean
		caps?: Pick<SecurePurchaseCapabilities, 'protocols' | 'protocol_status'> | null
		provenProtocol?: SecureAssetProtocol | null
	} = {}
): ListingPlan {
	if (SHAPE_CODES.has(code)) {
		if (!opts.shapeConfirmed) return { action: 'confirm', message: shapeReason(code) }
		return { action: 'standard', note: shapeReason(code), shape: true }
	}
	if (code === 'protocol_mismatch') {
		if (opts.provenProtocol && protocolProtectionEnabled(opts.provenProtocol, opts.caps)) {
			return { action: 'block', message: SNIPE_TEMPORARILY_UNAVAILABLE }
		}
		return { action: 'standard', note: NO_SNIPE_PROTECTION_NOTE, shape: false }
	}
	if (PROTECTION_DOWN_CODES.has(code)) {
		return { action: 'block', message: SNIPE_TEMPORARILY_UNAVAILABLE }
	}
	return { action: 'block', message: '' }
}

export function protectedErrorCode(error: unknown): string | null {
	if (!error || typeof error !== 'object') return null
	const direct = (error as { code?: unknown }).code
	if (typeof direct === 'string' && direct) return direct
	const body = (error as { response?: { data?: { code?: unknown } } }).response?.data?.code
	return typeof body === 'string' && body ? body : null
}

/** Slug to put on a secure-listing item so telemetry and protocol detection agree. */
export function collectionSlugOf(row: any, fallback?: string | null): string | null {
	const slug = row?.collection?.slug || row?.collection_slug || fallback || null
	return typeof slug === 'string' && slug ? slug : null
}
